/**
 *  Helper methods for processing dates.
 */
import i18n from "@/plugins/vi18n";

/**
 * Converts a date string to the international date format.
 * @param {Date | string} date
 * @return {string}
 */
export const convertDateFormatToYmd = (date: Date | string) => {
    if (typeof date === "string") {
        date = new Date(date);
    }
    const day = date.getDate();
    const month = date.getMonth() + 1; // months are zero-based, so we add 1.
    const year = date.getFullYear();

    /** Convert day and month to strings and pad single-digit values with leading zeros*/
    const paddedDay = day < 10 ? "0" + day : day.toString();
    const paddedMonth = month < 10 ? "0" + month : month.toString();

    /** Format the date as Y-m-d */
    const formattedDate = `${year}-${paddedMonth}-${paddedDay}`;

    return formattedDate;
};

/**
 * Converts a date string to return DD/MM.
 * @param {Date | string} date
 * @return {string}
 */
export const convertDateFormatToDDMM = (date: Date | string) => {
    if (typeof date === "string") {
        date = new Date(date);
    }
    const day = date.getDate();
    const month = date.getMonth() + 1; // months are zero-based, so we add 1.

    /** Convert day and month to strings and pad single-digit values with leading zeros*/
    const paddedDay = day < 10 ? "0" + day : day.toString();
    const paddedMonth = month < 10 ? "0" + month : month.toString();

    /** Format the date as Y-m-d */
    const formattedDate = `${paddedDay}/${paddedMonth}`;

    return formattedDate;
};

export const getDateInPast = (years: number) => {
    /**
     * Get a Date object representing a date in the past.
     * @param {number} years - The number of years to subtract from the current year.
     * @returns {Date} A Date object representing the past date.
     */
    const today = new Date();
    today.setFullYear(today.getFullYear() - years);
    return today;
};

export const hoursUntil = (utcDate: string): number => {
    /**
     * Get a Date UTC string and calculates how many hours are left until that date.
     * @param {number} utcDate - The number of years to subtract from the current year.
     * @returns {Date} A number representing the hours until that date.
     */
    const formattedDate = utcDate.split(".")[0] + "Z";
    const targetDate = new Date(formattedDate);
    const now = new Date();

    const diffMs = targetDate.getTime() - now.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60)); // Rounding down the result
};

export const formatUTCDate = (utcDate: string): string => {
    /**
     * Formats a UTC date string into a user-friendly format: DD MM YYYY HH:MM.
     * This function converts the UTC date string to a Date object, then extracts and
     * formats the day, month, year, hours, and minutes in the desired format.
     *
     * @param {string} utcDate
     * @returns {string}
     */
    const formattedDate = utcDate.split(".")[0] + "Z";
    const targetDate = new Date(formattedDate);

    const day = String(targetDate.getDate()).padStart(2, "0");
    const month = String(targetDate.getMonth() + 1).padStart(2, "0");
    const year = targetDate.getFullYear();
    const hours = String(targetDate.getHours()).padStart(2, "0");
    const minutes = String(targetDate.getMinutes()).padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes}`;
};

export const isActiveNow = (
    activeFrom?: string,
    activeTo?: string,
): boolean => {
    /**
     * Checks if the current date falls within the specified active date range.
     * Handles cases where:
     * - Both dates are provided
     * - Only one date is provided
     * - Neither date is provided
     * @param { string } activeFrom
     * @param { string } activeTo
     * @returns { boolean }
     */
    const now = new Date();

    // Parse the input dates, or set to null if not provided
    const fromDate = activeFrom ? new Date(activeFrom) : null;
    const toDate = activeTo ? new Date(activeTo) : null;

    // both dates provided
    if (fromDate && toDate) {
        return now >= fromDate && now <= toDate;
    }

    // only fromDate provided, active from that date onward
    if (fromDate && !toDate) {
        return now >= fromDate;
    }

    // only toDate exists, active until that date
    if (!fromDate && toDate) {
        return now <= toDate;
    }

    // If neither date exists, consider active
    return true;
};

export const getPaymentLinkAvailabilityMessage = (
    activeFrom?: string,
    activeTo?: string,
): string | null => {
    /**
     * Returns a user-facing message based on the availability of a payment link,
     * using the provided activeFrom and activeTo dates.
     * @param { string } activeFrom
     * @param { string } activeTo
     * @returns { string | null }
     */
    const now = new Date();
    const fromDate = activeFrom ? new Date(activeFrom) : null;
    const toDate = activeTo ? new Date(activeTo) : null;

    // If both dates exist
    if (fromDate && toDate) {
        if (now < fromDate) {
            return i18n.global.t("default.paymentLinkNotActiveYet", {
                fromDate: fromDate.toLocaleDateString(),
            });
        }
        if (now > toDate) {
            return i18n.global.t("default.paymentLinkExpired", {
                toDate: toDate.toLocaleDateString(),
            });
        }
        return null;
    }

    // If only fromDate exists
    if (fromDate && !toDate) {
        if (now < fromDate) {
            return i18n.global.t("default.paymentLinkNotActiveYet", {
                fromDate: fromDate.toLocaleDateString(),
            });
        }
        return null;
    }

    // If only toDate exists
    if (!fromDate && toDate) {
        if (now > toDate) {
            return i18n.global.t("default.paymentLinkExpired", {
                toDate: toDate.toLocaleDateString(),
            });
        }
        return null;
    }

    // If neither date exists, default to always available
    return null;
};

/**
 * Converts a DD/MM/YYYY date string to YYYY-MM-DD
 * @param { string } input - date string in DD/MM/YYYY format
 * @returns { string } - date string in YYYY-MM-DD format
 */
export const formatDateToYYYYMMDD = (input: string): string => {
    return input.split("/").reverse().join("-");
};

/**
 * Determines the urgency state of a quote based on its expiry date.
 * @param {string} quoteExpiryDate - The expiry date string of the quote.
 * @param {number} nowMs - The current time in milliseconds (e.g. Date.now()).
 * @returns {"normal" | "warning" | "danger" | "expired"} The urgency state.
 */
export const getQuoteUrgencyState = (
    quoteExpiryDate: string,
    nowMs: number,
): "normal" | "warning" | "danger" | "expired" => {
    const formattedDate = quoteExpiryDate.split(".")[0] + "Z";
    const expiryTime = new Date(formattedDate).getTime();
    const diffMs = expiryTime - nowMs;

    if (diffMs <= 0) return "expired";

    const totalMinutes = diffMs / 60000;
    if (totalMinutes <= 2) return "danger";
    if (totalMinutes <= 10) return "warning";
    return "normal";
};

/**
 * Formats a duration in milliseconds into a human-readable countdown string.
 * Output format depends on magnitude: "1d 02:30:15", "01:30:45", or "05:30".
 * @param {number} diffMs - The duration in milliseconds.
 * @returns {string} Formatted countdown string.
 */
export const formatCountdown = (diffMs: number): string => {
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (days > 0) {
        return `${days}d ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    if (hours > 0) {
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

/**
 * Formats a UTC date string into a human-readable date and time string in UTC.
 * Includes both the date and time components, e.g. "22 Jul 2026 14:30 UTC".
 * Useful for displaying expiry times where the user needs both date and time context.
 * @param {string} utcDate - The raw UTC date string (with or without milliseconds).
 * @returns {string | null} Formatted date-time string, or null if the input is invalid.
 */
export const formatUTCDateTimeForDisplay = (
    utcDate: string,
    locale?: string,
): string | null => {
    const date = new Date(utcDate.split(".")[0] + "Z");
    if (isNaN(date.getTime())) return null;
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = date.toLocaleString(locale, {
        month: "short",
        timeZone: "UTC",
    });
    const year = date.getUTCFullYear();
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    return `${day} ${month} ${year} ${hours}:${minutes} UTC`;
};

export const formatDateToDDMMYYYY = (input: string): string => {
    /**
     * Converts a date to DD/MM/YYYY
     * @param { string } input - date string
     * @returns { string } - date string in DD/MM/YYYY format
     * */
    const date = new Date(input);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
};
